import { useCallback, useEffect } from "react";
import { setValidationState } from "reducers/main";
import { useParams } from "react-router-dom";
import Card from "components/Card/Card";
import { useDispatch, useSelector } from "utils/redux";
import { ControlledDateInput } from "../../components/Card/ControlledInput";

import { setRRE } from "reducers/rre.ts";
import { useForm, Controller } from "react-hook-form";
import RREPageContainer from "./RREPageContainer";
import type { OptionType } from "components/inputs";
import { TextInput, Checkbox, Select, MultiSelect } from "components/inputs";

import {
  useGetRreValidationsQuery,
  usePatchRreByIdMutation,
} from "services/rre-api";

const RREInformation = ({
  constants,
  control,
  errors,
  focusedField,
  handleSave,
  getValues,
  supportsClientFeedExclusion,
}) => {
  return (
    <Card heading="RRE Information" cardNumber={1}>
      <Controller
        control={control}
        name={"rre_id"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"RRE ID"}
            label="CMS RRE ID"
            errors={errors}
            readOnly={true}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("rre_id", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name={"name"}
        render={({ field }) => (
          <TextInput
            {...field}
            label="RRE Name"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("name", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name={"street"}
        render={({ field }) => (
          <TextInput
            {...field}
            label="Address"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("street", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name={"city"}
        render={({ field }) => (
          <TextInput
            {...field}
            label="City"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("city", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name={"state"}
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="State"
            name={name}
            errors={errors}
            isFocused={focusedField === name}
            options={constants?.STATES || []}
            value={constants?.STATES?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("state", o.value);
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={"zipcode"}
        render={({ field }) => (
          <TextInput
            {...field}
            label="Zip Code"
            placeholder={"X".repeat(5)}
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("zipcode", getValues(field.name))}
          />
        )}
      />
      <Controller
        key={"reporting_existed_prior_to_onboarding"}
        name="reporting_existed_prior_to_onboarding"
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Checkbox
            label="Reporting history existed prior to onboarding"
            name="reporting_existed_prior_to_onboarding"
            checked={value}
            isFocused={focusedField === name}
            readOnly={true}
            onChange={(e) => {
              onChange(e.target.checked);
              handleSave(
                "reporting_existed_prior_to_onboarding",
                e.target.checked,
              );
            }}
          />
        )}
      />
      <Controller
        key={"excluded_from_outbound_feeds"}
        name="excluded_from_outbound_feeds"
        control={control}
        render={({ field: { value, name, onChange } }) => (
          <Checkbox
            label="Exclude from outbound CMS feeds (query, claim, TIN)"
            name="excluded_from_outbound_feeds"
            checked={value}
            isFocused={focusedField === name}
            onChange={(e) => {
              onChange(e.target.checked);
              handleSave("excluded_from_outbound_feeds", e.target.checked);
            }}
          />
        )}
      />
      {supportsClientFeedExclusion && (
        <Controller
          key={"excluded_from_client_feeds"}
          name="excluded_from_client_feeds"
          control={control}
          render={({ field: { value, name, onChange } }) => (
            <Checkbox
              label="Exclude from outbound client feeds (e.g. weekly claim status file)"
              name="excluded_from_client_feeds"
              checked={value}
              isFocused={focusedField === name}
              onChange={(e) => {
                onChange(e.target.checked);
                handleSave("excluded_from_client_feeds", e.target.checked);
              }}
            />
          )}
        />
      )}
    </Card>
  );
};

const CompanyInformation = ({
  constants,
  control,
  errors,
  focusedField,
  handleSave,
  getValues,
}) => {
  const planInsuranceTypes = constants?.PLAN_INSURANCE_TYPE?.filter(
    (option: OptionType) => !!option.value,
  );
  return (
    <Card heading="Company Information" cardNumber={2}>
      <ControlledDateInput
        control={control}
        allowFutureDates={true}
        label={"Report Date"}
        name={"profile_report_date"}
        errors={errors}
        onSave={(value) => handleSave("profile_report_date", value)}
      />
      <ControlledDateInput
        control={control}
        allowFutureDates={true}
        label={"Profile Change Date"}
        name={"profile_changed_date"}
        errors={errors}
        onSave={(value) => handleSave("profile_changed_date", value)}
      />

      <Controller
        control={control}
        name={"naic"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"NAIC"}
            label="NAIC"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("naic", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name={"lines_of_business"}
        render={({ field: { name, value, onChange } }) => (
          <MultiSelect
            label="Lines of Business"
            name={name}
            errors={errors}
            disableRemoveCondition={false}
            isFocused={focusedField === name}
            options={planInsuranceTypes}
            value={planInsuranceTypes?.filter((option) =>
              value?.map((v) => v).includes(option.value),
            )}
            onChange={(options: OptionType[]) => {
              onChange(options);
              handleSave(
                "lines_of_business",
                options.map((option) => option.value),
              );
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={"reporter_type"}
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="Reporting Type"
            name={name}
            errors={errors}
            isFocused={focusedField === name}
            options={constants?.RRE_REPORTER_TYPES || []}
            value={constants?.RRE_REPORTER_TYPES?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("reporter_type", o.value);
            }}
          />
        )}
      />
      <Controller
        control={control}
        name={"operations_assignee"}
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="Operations Assignee"
            name={name}
            errors={errors}
            isFocused={focusedField === name}
            options={constants?.OPERATIONS_USERS || []}
            value={constants?.OPERATIONS_USERS?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("operations_assignee", o.value);
            }}
          />
        )}
      />
    </Card>
  );
};

const AuthorizedRepresentative = ({
  constants,
  control,
  errors,
  focusedField,
  handleSave,
  getValues,
}) => {
  return (
    <Card heading="Authorized Representative" cardNumber={3}>
      <Controller
        control={control}
        name={"contact_name"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Contact Name"}
            label="Name"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("contact_name", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name={"contact_title"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Contact Title"}
            label="Title"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("contact_title", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name={"contact_email"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Contact Email"}
            label="Email"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("contact_email", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name="contact_phone"
        render={({ field }) => (
          <TextInput
            {...field}
            label="Phone"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("contact_phone", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name="contact_street"
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Contact Street"}
            label="Address"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("contact_street", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name="contact_city"
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Contact City"}
            label="City"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("contact_city", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name="contact_state"
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="State"
            name={name}
            errors={errors}
            isFocused={focusedField === name}
            options={constants?.STATES || []}
            value={constants?.STATES?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("contact_state", o.value);
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="contact_zipcode"
        render={({ field }) => (
          <TextInput
            {...field}
            label="Zip"
            placeholder={"X".repeat(5)}
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("contact_zipcode", getValues(field.name))}
          />
        )}
      />
    </Card>
  );
};

const AccountManager = ({
  constants,
  control,
  errors,
  focusedField,
  handleSave,
  getValues,
}) => {
  return (
    <Card heading="Account Manager" cardNumber={4}>
      <Controller
        control={control}
        name={"account_mgr_name"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Name"}
            label="Name"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("account_mgr_name", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name={"account_mgr_title"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Title"}
            label="Title"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("account_mgr_title", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name={"account_mgr_email"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Email"}
            label="Email"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("account_mgr_email", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name="account_mgr_phone"
        render={({ field }) => (
          <TextInput
            {...field}
            label="Phone"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("account_mgr_phone", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name="account_mgr_street"
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Street"}
            label="Address"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("account_mgr_street", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name="account_mgr_city"
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"City"}
            label="City"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() => handleSave("account_mgr_city", getValues(field.name))}
          />
        )}
      />
      <Controller
        control={control}
        name="account_mgr_state"
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="State"
            name={name}
            errors={errors}
            isFocused={focusedField === name}
            options={constants?.STATES || []}
            value={constants?.STATES?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("account_mgr_state", o.value);
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="account_mgr_zipcode"
        render={({ field }) => (
          <TextInput
            {...field}
            label="Zip Code"
            placeholder={"X".repeat(5)}
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("account_mgr_zipcode", getValues(field.name))
            }
          />
        )}
      />
    </Card>
  );
};

const SubmissionAgent = ({
  constants,
  control,
  errors,
  focusedField,
  handleSave,
  getValues,
}) => {
  return (
    <Card heading="Submission Agent" cardNumber={5}>
      <Controller
        control={control}
        name={"submission_agent_name"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Name"}
            label="Name"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("submission_agent_name", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name={"submission_agent_title"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Title"}
            label="Title"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("submission_agent_title", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name={"submission_agent_email"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Email"}
            label="Email"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("submission_agent_email", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name={"submission_agent_phone"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Phone"}
            label="Phone"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("submission_agent_phone", getValues(field.name))
            }
          />
        )}
      />

      <Controller
        control={control}
        name="submission_agent_street"
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Street"}
            label="Address"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("submission_agent_street", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name="submission_agent_city"
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"City"}
            label="City"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("submission_agent_city", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name="submission_agent_state"
        render={({ field: { name, value, onChange } }) => (
          <Select
            label="State"
            name={name}
            errors={errors}
            isFocused={focusedField === name}
            options={constants?.STATES || []}
            value={constants?.STATES?.find(
              (option: OptionType) => option.value === value,
            )}
            onChange={(o: OptionType) => {
              onChange(o.value);
              handleSave("submission_agent_state", o.value);
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="submission_agent_zipcode"
        render={({ field }) => (
          <TextInput
            {...field}
            label="Zip Code"
            placeholder={"X".repeat(5)}
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("submission_agent_zipcode", getValues(field.name))
            }
          />
        )}
      />
    </Card>
  );
};

const EDIRep = ({ control, errors, focusedField, handleSave, getValues }) => {
  return (
    <Card heading="EDI Representative" cardNumber={5}>
      <Controller
        control={control}
        name={"bcrc_edi_rep_name"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Name"}
            label="Name"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("bcrc_edi_rep_name", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name={"bcrc_edi_rep_email"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Email"}
            label="Email"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("bcrc_edi_rep_email", getValues(field.name))
            }
          />
        )}
      />
      <Controller
        control={control}
        name={"bcrc_edi_rep_phone"}
        render={({ field }) => (
          <TextInput
            {...field}
            placeholder={"Phone"}
            label="Phone"
            errors={errors}
            isFocused={focusedField === field.name}
            onBlur={() =>
              handleSave("bcrc_edi_rep_phone", getValues(field.name))
            }
          />
        )}
      />
    </Card>
  );
};

const RREOverview = () => {
  const { rreId } = useParams<{ rreId: string }>();
  const dispatch = useDispatch();

  const {
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm();
  const rre = useSelector((state) => state.rre.currentRRE);

  useEffect(() => {
    if (rre) {
      reset(rre);
    }
  }, [rre, reset]);

  const { data: rreValidation } = useGetRreValidationsQuery(
    { id: rreId },
    { skip: !rreId },
  );

  useEffect(() => {
    if (rreValidation) {
      const validationData = Object.values(rreValidation).flat();
      const errors = validationData.filter((item) => item.level === "ERROR");
      const warnings = validationData.filter((item) => item.level === "WARN");
      dispatch(
        setValidationState({
          errors: errors,
          warnings: warnings,
        }),
      );
    }
  }, [dispatch, rreValidation]);

  const [patchRreById] = usePatchRreByIdMutation();

  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      try {
        await patchRreById({
          rreId: rreId,
          data: field === "" ? value : { [field]: value },
        })
          .unwrap()
          .then((result) => {
            dispatch(setRRE(result));
          });
      } catch (error) {
        console.error("Save failed:", error);
      }
    },
    [patchRreById, dispatch, rreId],
  );

  const constants = useSelector((state) => state.shared.constants);
  const focusedField = useSelector((state) => state.main.focusedField);

  return (
    <RREPageContainer>
      <div key={`claim-overview-form`}>
        <div className="grid-layout gl-2 no-gap-tb">
          <div>
            <RREInformation
              constants={constants}
              control={control}
              errors={errors}
              focusedField={focusedField}
              handleSave={handleSave}
              getValues={getValues}
              supportsClientFeedExclusion={
                rre?.supports_client_feed_exclusion ?? false
              }
            />
            <CompanyInformation
              constants={constants}
              control={control}
              errors={errors}
              focusedField={focusedField}
              handleSave={handleSave}
              getValues={getValues}
            />
            <AuthorizedRepresentative
              constants={constants}
              control={control}
              errors={errors}
              focusedField={focusedField}
              handleSave={handleSave}
              getValues={getValues}
            />
          </div>
          <div>
            <AccountManager
              constants={constants}
              control={control}
              errors={errors}
              focusedField={focusedField}
              handleSave={handleSave}
              getValues={getValues}
            />

            <SubmissionAgent
              constants={constants}
              control={control}
              errors={errors}
              focusedField={focusedField}
              handleSave={handleSave}
              getValues={getValues}
            />
            <EDIRep
              control={control}
              errors={errors}
              focusedField={focusedField}
              handleSave={handleSave}
              getValues={getValues}
            />
          </div>
        </div>
      </div>
    </RREPageContainer>
  );
};

export default RREOverview;
